import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { injectChatAreaStyles } from './styles';
import { isValidContent } from './utils/contentHelpers';
import { processToolCallMessages } from './utils/messageHelpers';

// Import hooks
import { 
  useAutoScroll, 
  useSearch, 
  useMessageHandling, 
  useFileUpload 
} from './hooks';
import { useAuthenticatedApi } from '../../../hooks/useAuthenticatedApi';

// Import components
import MessageList from './message-display';
import SearchPanel from './search';
import LoadingOverlay from './overlays/LoadingOverlay';
import ErrorIndicator from './overlays/ErrorIndicator';
import UploadedFiles from './input-area/UploadedFiles';
import InputArea from './input-area';
import { ContextWarning, ScrollToBottomButton } from './overlays';

/**
 * ChatArea component - Main chat interface
 * Refactored version with modular components
 */
const ChatArea = ({
  messages = [],
  input,
  setInput,
  handleChatSubmit,
  agentStatus,
  isEventConnected,
  handleStopGeneration,
  latestSystemMessage,
  showLLMErrorIndicator,
  LLMErrorIndicatorMessage,
  contextUsage,
  createThread,
  navigate,
  workspaceId,
  currentAgentType,
  isSandboxReady,
  // Additional props that might be passed
  fileInputRef: externalFileInputRef,
  handleFileUpload: externalHandleFileUpload,
  removeFile: externalRemoveFile,
  uploadedFiles: externalUploadedFiles,
  setUploadedFiles: externalSetUploadedFiles,
  expandedTools: externalExpandedTools,
  toggleToolExpansion: externalToggleToolExpansion,
  chatRef: externalChatRef,
  readOnly = false
}) => {
  // State for dismissed messages
  const [dismissedErrorMessage, setDismissedErrorMessage] = useState(null);
  const [dismissedContextUsage, setDismissedContextUsage] = useState(null);
  const [panelHeight, setPanelHeight] = useState(0);

  // Refs
  const internalChatRef = useRef(null);
  const chatRef = externalChatRef || internalChatRef;
  const inputAreaRef = useRef(null);

  // Inject styles on mount
  useEffect(() => {
    injectChatAreaStyles();
  }, []);

  // Track panel height changes with ResizeObserver
  useEffect(() => {
    if (!inputAreaRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setPanelHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(inputAreaRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Process messages to handle tool calls and filter valid content
  const validMessages = useMemo(() => {
    // First filter to include all valid messages and tool messages
    const filteredMessages = messages.filter(message => {
      // Always include tool-related messages for processing
      const messageType = message.message_type;
      if (messageType === 'tool' || messageType === 'tool_call' || messageType === 'tool_result' || messageType === 'tool_error') {
        return true;
      }
      return isValidContent(message.content);
    });
    
    // Then process tool calls to group them with results
    const processed = processToolCallMessages(filteredMessages);
    
    // Debug logging for streaming tool calls
    const streamingTools = processed.filter(m => m.toolStatus?.isStreaming || m.toolStatus?.streamingContent);
    if (streamingTools.length > 0) {
      console.log('ChatArea - Streaming tools after processing:', streamingTools.map(m => ({
        index: processed.indexOf(m),
        toolCallId: m.tool_call_id || m.toolCallId,
        isToolCall: m.isToolCall,
        status: m.toolStatus?.status,
        isStreaming: m.toolStatus?.isStreaming,
        hasStreamingContent: !!m.toolStatus?.streamingContent,
        streamingContentLength: m.toolStatus?.streamingContent?.length
      })));
    }
    
    return processed;
  }, [messages]);

  // Use custom hooks
  const authenticatedApi = useAuthenticatedApi();
  const autoScroll = useAutoScroll(chatRef, validMessages, agentStatus);
  const search = useSearch(validMessages);
  const messageHandling = useMessageHandling();
  const fileUpload = useFileUpload();

  // Use external props if provided, otherwise use hook values
  const expandedTools = externalExpandedTools || messageHandling.expandedTools;
  const toggleToolExpansion = externalToggleToolExpansion || messageHandling.toggleToolExpansion;
  const fileInputRef = externalFileInputRef || fileUpload.fileInputRef;
  const handleFileUpload = externalHandleFileUpload || fileUpload.handleFileUpload;
  const removeFile = externalRemoveFile || fileUpload.removeFile;
  const uploadedFiles = externalUploadedFiles || fileUpload.uploadedFiles;
  const setUploadedFiles = externalSetUploadedFiles || fileUpload.setUploadedFiles;

  // System message for loading overlay
  const systemMessageForOverlay = useMemo(() => {
    return latestSystemMessage || null;
  }, [latestSystemMessage]);

  // Error dismissal
  const dismissError = useCallback(() => {
    setDismissedErrorMessage(LLMErrorIndicatorMessage);
  }, [LLMErrorIndicatorMessage]);

  // Context warning dismissal
  const dismissContext = useCallback(() => {
    setDismissedContextUsage(contextUsage);
  }, [contextUsage]);

  // New thread creation
  const handleNewThread = useCallback(async () => {
    if (!createThread || !navigate || !workspaceId) return;
    
    try {
      const threadData = {
        title: `New ${currentAgentType || 'Coding'} Thread`,
        agent_type: currentAgentType === 'Coding' ? 'coding_agent' : 
                     currentAgentType === 'Browser' ? 'browser_agent' : 
                     currentAgentType === 'Research' ? 'research_agent' : 'coding_agent',
        auto_submit_first_message: false,
        message_history: []
      };
      
      const createdThread = await createThread(workspaceId, threadData, authenticatedApi);
      
      if (createdThread && createdThread.id) {
        navigate(`/project/${workspaceId}/thread/${createdThread.id}`);
      }
    } catch (error) {
      console.error('Error creating new thread:', error);
    }
  }, [createThread, navigate, workspaceId, currentAgentType, authenticatedApi]);


  // Calculate available height for input area
  const availableInputHeight = useMemo(() => {
    if (!panelHeight) return 200;
    const contextWarningHeight = contextUsage?.percentage > 80 && !dismissedContextUsage ? 60 : 0;
    const result = Math.max(panelHeight - contextWarningHeight - 20, 80); // 20px for padding, min 80px
    return result;
  }, [panelHeight, contextUsage, dismissedContextUsage]);

  // Reset dismissed error when a new error appears
  useEffect(() => {
    if (LLMErrorIndicatorMessage && LLMErrorIndicatorMessage !== dismissedErrorMessage) {
      setDismissedErrorMessage(null);
    }
  }, [LLMErrorIndicatorMessage, dismissedErrorMessage]);

  // Reset dismissed context when context usage changes
  useEffect(() => {
    if (contextUsage && JSON.stringify(contextUsage) !== JSON.stringify(dismissedContextUsage)) {
      setDismissedContextUsage(null);
    }
  }, [contextUsage, dismissedContextUsage]);

  return (
    <PanelGroup direction="vertical" className="h-full">
     <Panel defaultSize={75} minSize={40} className="relative">
      <div 
        className="k-chat-area overflow-y-auto bg-slate-100 dark:bg-neutral-950/40 chat-area-scrollbar h-full" 
        ref={chatRef}
        onScroll={autoScroll.enhancedHandleScroll}
        onWheel={autoScroll.handleWheelScroll}
        onTouchStart={autoScroll.handleScrollStart}
        onTouchMove={autoScroll.handleScrollStart}
        data-tour="chat-messages"
        style={{
          scrollBehavior: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <SearchPanel 
          {...search}
        />
        <MessageList 
          validMessages={validMessages}
          copiedMessageId={messageHandling.copiedMessageId}
          copyToClipboard={messageHandling.copyToClipboard}
          expandedTools={expandedTools}
          toggleToolExpansion={toggleToolExpansion}
          agentStatus={agentStatus}
          searchQuery={search.searchQuery}
          activeSearchResult={search.activeSearchResult}
        />
        <ScrollToBottomButton 
          isNearBottom={autoScroll.isNearBottom}
          scrollToBottom={autoScroll.scrollToBottom}
        />
        <LoadingOverlay
          isEventConnected={isEventConnected}
          agentStatus={agentStatus}
          validMessagesCount={validMessages.length}
          systemMessage={systemMessageForOverlay}
        />
      </div>
      
      {/* Overlays positioned above the panel divider */}
      <div className="absolute bottom-2 left-4 right-4 pointer-events-none z-30 space-y-2">
        <div className="pointer-events-auto backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80 rounded-xl">
          <ErrorIndicator 
            showLLMErrorIndicator={showLLMErrorIndicator}
            LLMErrorIndicatorMessage={LLMErrorIndicatorMessage}
            dismissedErrorMessage={dismissedErrorMessage}
            onDismiss={dismissError}
          />
        </div>
        
        <div className="pointer-events-auto backdrop-blur-sm bg-white/80 dark:bg-neutral-900/80 rounded-xl">
          <ContextWarning
            contextUsage={contextUsage}
            agentStatus={agentStatus}
            dismissedContextUsage={dismissedContextUsage}
            onDismiss={dismissContext}
            onNewThread={handleNewThread}
          />
        </div>
        
        {uploadedFiles.length > 0 && (
          <div className="pointer-events-auto">
            <UploadedFiles 
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              removeFile={removeFile}
            />
          </div>
        )}
      </div>
    </Panel>
      
    <PanelResizeHandle className="h-2 bg-transparent hover:bg-brand-primary/20 transition-colors duration-200 flex items-center justify-center group">
      <div className="w-full h-0.5 bg-neutral-300 dark:bg-neutral-600 group-hover:bg-brand-primary/40 transition-colors duration-200"></div>
    </PanelResizeHandle>
    
    <Panel defaultSize={15} minSize={10} maxSize={60} className="landscape:min-h-0">
      <div 
        ref={inputAreaRef}
        className="k-chat-area h-full py-2 landscape:py-1 px-4 landscape:px-2 bg-slate-100 dark:bg-neutral-950/40 relative"
      >
        <InputArea
          input={input}
          setInput={setInput}
          handleChatSubmit={handleChatSubmit}
          fileInputRef={fileInputRef}
          handleFileUpload={handleFileUpload}
          agentStatus={agentStatus}
          isEventConnected={isEventConnected}
          isSandboxReady={isSandboxReady}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          handleStopGeneration={handleStopGeneration}
          removeFile={fileUpload.removeFile}
          availableHeight={availableInputHeight}
          readOnly={readOnly}
        />
      </div>
    </Panel>
  </PanelGroup>
  );
};

export default ChatArea;
